import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import { Field } from 'formik';
import { Button } from '@storable/canaveral-components';
import FormFieldText from '../FormFieldText';
import iconMap from './iconMap';
import styles from './styles.scss';

const propTypes = {
  limit: PropTypes.number,
  setFieldValue: PropTypes.func.isRequired,
  values: PropTypes.shape({
    socialMediaPlatforms: PropTypes.arrayOf({
      socialMediaPlatform: PropTypes.shape({
        enabled: PropTypes.bool.isRequired,
        link: PropTypes.string,
      }),
    }).isRequired,
  }).isRequired,
  iconColor: PropTypes.string.isRequired,
  title: PropTypes.string,
};

const defaultProps = {
  limit: 5,
  title: undefined,
};

const getName = (field, provider) => `socialMediaPlatforms.${provider}.${field}`;
const SocialMediaIconForm = ({
  limit,
  setFieldValue,
  values,
  iconColor,
  title,
}) => {
  const [numChecked, setNumChecked] = useState(0);

  useEffect(() => {
    const getEnabled = Object.entries(values.socialMediaPlatforms).reduce((acc, [key, value]) => {
      if (value.enabled) {
        // since the legacy widget editor allowed for more than 5 social media platforms
        // we need to handle that case by disabling them in the form so users can't save
        // more than 5 going forward
        if (acc >= limit) {
          setFieldValue(getName('enabled', key), false);
        }
        return acc + 1;
      }
      return acc;
    }, 0);
    setNumChecked(getEnabled);
  }, [values.socialMediaPlatforms]);

  const renderFormGroup = (provider) => {
    const filteredPlatform = values.socialMediaPlatforms && values.socialMediaPlatforms[provider];
    const checked = filteredPlatform ? filteredPlatform.enabled : false;

    return (
      <Row key={provider} className={styles.fieldRow} data-testid="facility-edit-form-social">
        <Col sm={4} className={styles.checkboxCol}>
          <Field
            type="checkbox"
            className={styles.checkbox}
            name={getName('enabled', provider)}
            checked={filteredPlatform ? filteredPlatform.enabled : false}
            disabled={!checked && numChecked >= limit}
          />
          <span className={styles.socialMediaName}>
            {iconMap[provider].displayName}
            {iconMap[provider].subText && (
              <span className={styles.socialMediaSubText}>
                            {iconMap[provider].subText}
              </span>
            )
            }
          </span>
        </Col>
        <Col sm={7} className={styles.linkCol}>
          <FormFieldText
            name={getName('link', provider)}
            className={styles.link}
            hideLabel
            required={checked}
          />
        </Col>
        <Col sm={1} className={styles.iconCol}>
          <Button
            className={styles.icon}
            style={{ backgroundColor: iconColor, color: 'white' }}
            icon={iconMap[provider].icon}
            label={`${iconMap[provider].displayName}`}
            size="sm"
            shape="circle"
            context="bare"
            borderless
          />
        </Col>
      </Row>
    );
  };

  const renderSocialMediaFields = () => (
    Object.keys(iconMap).map(renderFormGroup)
  );

  return (
    <Row>
      <Col xs={12} className={styles.title}>
        <span>
          {title || 'Social Media Accounts'}
        </span>
      </Col>
      <Col xs={12} className={styles.label}>
        <span>
          The settings below control which social media icons and links appear in the
          social media widget. Select up to five social media links to display.
        </span>
      </Col>
      <Col xs={12} className={styles.fieldsCol}>
        {renderSocialMediaFields()}
      </Col>
    </Row>
  );
};

SocialMediaIconForm.propTypes = propTypes;
SocialMediaIconForm.defaultProps = defaultProps;

export default SocialMediaIconForm;
